import { type Register, useMatches } from '@tanstack/react-router';
import type { RouteIds } from '@tanstack/router-core';
import { useCurrentUser } from 'hooks/slices/useCurrentUser';
import { useEffect } from 'react';
import { useAuthStore } from 'stores/authStore';
import { useNavigationStore } from 'stores/navigationStore';
import { tracker } from './tracker';
import { useTrackingContext } from './useTrackingContext';

type RouterType = Register['router'];
type RouteId = RouteIds<RouterType['routeTree']>;

/**
 * Name of tracked pages by their route ID.
 * Leave undefined for routes that should not be tracked.
 *
 * **Note**: Please keep this object sorted (there's a vscode extension for that).
 */
const trackedNameByRoute: Record<RouteId, string | undefined> = {
  __root__: undefined, // Undefined routes are not tracked
  '/': undefined,
  '/*': undefined,
  '/403': 'Unauthorized',
  '/404': 'NotFound',
  '/498': 'InvalidToken',
  '/500': 'Error',
  '/admin': undefined,
  '/admin/': undefined,
  '/admin/admin_tasks': 'AdminTasks',
  '/admin/appointment-note-templates': 'AppointmentNoteTemplates',
  '/admin/claim_relaunch_plans': 'ClaimRelaunchPlans',
  '/admin/claim_relaunch_plans/$id/edit': 'ClaimRelaunchPlanEdit',
  '/admin/claim_relaunch_plans/new': 'ClaimRelaunchPlanNew',
  '/admin/expired_products_requests': 'ExpiredProductsRequests',
  '/admin/expired_products_requests/$id': 'ExpiredProductsRequest',
  '/admin/faks': 'Faks',
  '/admin/groups': 'Groups',
  '/admin/groups/$id': 'Group',
  '/admin/groups/new': 'GroupNew',
  '/admin/home': 'Home',
  '/admin/inbound-mails': 'InboundMails',
  '/admin/invoices': 'Claims',
  '/admin/invoices/$id': 'Claim',
  '/admin/trades/master_trades': 'LabTrades',
  '/admin/trades/konnect': 'KonnectTrades',
  '/admin/trades/konnect/$id/edit': 'EditKonnectTrade',
  '/admin/trades/manual_validation': 'ValidatorTrades',
  '/admin/trades/manual_validation/validation': 'ValidatorTradeValidation',
  '/admin/trades/manual_validation/$masterTradeId/participations': 'ValidatorTradeParticipations',
  '/admin/labs': 'Labs',
  '/admin/labs/$id': 'Lab',
  '/admin/labs/new': 'LabNew',
  '/admin/payments': 'Payments',
  '/admin/pharmacies': 'Pharmacies',
  '/admin/pharmacies/$id': 'Pharmacy',
  '/admin/pharmacies/new': 'PharmacyNew',
  '/admin/kopilot/invoices': 'KopilotInvoices',
  '/admin/kopilot/invoices/$id': 'KopilotInvoice',
  '/admin/kopilot/validated-invoices': 'KopilotValidatedInvoices',
  // Legacy paths kept as redirect routes (FKS-8676); still in the route registry.
  '/admin/real_invoices': 'KopilotInvoices',
  '/admin/real_invoices/$id': 'KopilotInvoice',
  '/admin/settings/prompt-instructions': 'PromptInstructions',
  '/admin/tools/ai_prompt': 'AdminAiPrompt',
  '/admin/tools/datamatrix': 'Datamatrix',
  '/admin/tools/groups_labs': 'GroupsLabs',
  '/admin/tools/impersonate': 'Impersonate',
  '/admin/tools/ingestions': 'IngestionsHub',
  '/admin/tools/ingestions/new-ingestion': 'NewIngestion',
  '/admin/tools/marketing_tools': 'MarketingTools',
  '/admin/tools/marketing_tools/$id': 'MarketingTool',
  '/admin/tools/marketing_tools/new': 'MarketingToolNew',
  '/admin/tools/pharmacies/merge': 'PharmaciesMerge',
  '/admin/tools/referential_pharmacies': 'ReferentialPharmacies',
  '/admin/tools/sellout_panelist_generator': 'SelloutPanelistGenerator',
  '/admin/trades/instances': 'TradesInstances',
  '/admin/trades/instances/$id': 'TradeInstance',
  '/admin/trades': 'Trades',
  '/admin/trades/$id': 'Trade',
  '/admin/users': 'Users',
  '/admin/users/$id': 'User',
  '/document_signing_success': 'DocumentSigningSuccess',
  '/email_confirmation': 'EmailConfirmation',
  '/group': undefined,
  '/group/': undefined,
  '/group/$id/edit': 'GroupEdit',
  '/group/claims': 'Claims',
  '/group/claims/$id': 'Claim',
  '/group/dashboard': 'Dashboard',
  '/group/groups_labs': 'GroupsLabs',
  '/group/groups_labs/$id/edit': 'GroupsLabsEdit',
  '/group/pharmacies': 'Pharmacies',
  '/group/pharmacies/$id/edit': 'PharmacyEdit',
  '/group/pharmacy_lists': 'PharmacyLists',
  '/group/pharmacy_lists/$id': 'PharmacyList',
  '/group/pharmacy_lists/new': 'PharmacyListNew',
  '/group/trades': 'Trades',
  '/group/trades/$id': 'Trade',
  '/group/trades/$id/edit': 'TradeEdit',
  '/group/trades/new': 'TradeNew',
  '/lab': undefined,
  '/lab/': undefined,
  '/lab/$id/edit': 'EditLab',
  '/lab/claims/$id': 'SecuredClaimOld',
  '/lab/claims/dashboard': 'ClaimsDashboard',
  '/lab/claims/list': 'ClaimsList',
  '/lab/claims/list/$id': 'Claim',
  '/lab/expired_products_requests': undefined,
  '/lab/expired_products_requests/$id': 'ExpiredProductsRequest',
  '/lab/expired_products_requests/dashboard': 'ExpiredProductsRequestsDashboard',
  '/lab/expired_products_requests/list': 'ExpiredProductsRequestsList',
  '/lab/groups_labs': 'GroupsLabs',
  '/lab/groups_labs/$id/edit': 'GroupsLabEdit',
  '/lab/guide_items': 'GuideItems',
  '/lab/guide_items/$id/edit': 'GuideItemEdit',
  '/lab/guide_items/new': 'GuideItemNew',
  '/lab/pharmacies': 'Pharmacies',
  '/lab/pharmacies/$id': 'Pharmacy',
  '/lab/pharmacy_lists': 'PharmacyLists',
  '/lab/pharmacy_lists/$id': 'PharmacyList',
  '/lab/pharmacy_lists/new': 'PharmacyListNew',
  '/lab/regions/$id/edit': 'RegionEdit',
  '/lab/regions/new': 'RegionNew',
  '/lab/topics/$id/edit': 'TopicEdit',
  '/lab/topics/new': 'TopicNew',
  '/lab/trades/dashboard': 'TradesDashboard',
  '/lab/trades/list': 'Trades',
  '/lab/trades/master_trades': 'MasterTrades',
  '/lab/trades/master_trades/$id': 'MasterTrade',
  '/lab/trades/master_trades/new': 'MasterTradeNew',
  '/lab/trades/master_trades/wizard': 'MasterTradeWizard',
  '/login': undefined,
  '/login/': undefined,
  '/login/code': 'Code',
  '/login/email': 'Email',
  '/login/from_identity_provider': 'FromIdentityProvider',
  '/login/from_token': 'FromToken',
  '/login/invitation': 'Invitation',
  '/login/password/edit': 'PasswordEdit',
  '/login/password/reset': 'PasswordReset',
  '/login/phone_number': 'PhoneNumber',
  '/onboarder': undefined,
  '/onboarder/': undefined,
  '/onboarder/captchas': undefined,
  '/onboarder/pharmacies': 'Pharmacies',
  '/onboarder/pharmacies/$id/edit': 'PharmacyEdit',
  '/open-app': undefined,
  '/onboarding': undefined,
  '/onboarding/': undefined,
  '/onboarding/create_pharmacy': 'CreatePharmacy',
  '/onboarding/create_profile': 'CreateProfile',
  '/onboarding/join_pharmacy': 'JoinPharmacy',
  '/onboarding/lab_unavailable': 'LabUnavailable',
  '/onboarding/pharmacist_or_lab': 'PharmacistOrLab',
  '/onboarding/search_pharmacy': 'SearchPharmacy',
  '/onboarding/verify_account': 'VerifyAccount',
  '/onboarding/verify_email': 'VerifyEmail',
  '/outside_faks_signature': 'OutsideFaksSignature',
  '/redirect/digipharmacie/trades/participation/$participationId/attach-accounting-document':
    'AttachDigiAccountingDocument',
  '/redirect/trade/$tradeId': undefined,
  '/pharmacies/$pharmacy_id/expired_products_request/$id': 'ExpiredProductsRequest',
  '/pharmacy': undefined,
  '/pharmacy/': undefined,
  '/pharmacy/$id/edit': 'PharmacyEdit',
  '/pharmacy/appointment_notes': 'AppointmentNotes',
  '/pharmacy/claims': 'Claims',
  '/pharmacy/claims/$id': 'Claim',
  '/pharmacy/claims/expired_products_request/$id': 'ExpiredProductsRequest',
  '/pharmacy/claims/initiate': 'InitiateClaim',
  '/pharmacy/claims/new-claim': 'ClaimNewPage',
  '/pharmacy/compensations': 'Compensations',
  '/pharmacy/faks_konnect': 'FaksKonnect',
  '/pharmacy/group': 'Group',
  '/pharmacy/labs_discussions': 'LabsDiscussions',
  '/pharmacy/labs_discussions/$id': 'LabDiscussion',
  '/pharmacy/labs': 'Labs',
  '/pharmacy/labs/$id': 'Lab',
  '/pharmacy/labs/$id/kopilot/invoices/$invoiceId': 'KopilotInvoice',
  '/pharmacy/labs/$id/real_invoices/$invoiceId': 'KopilotInvoice',
  '/pharmacy/lgo_setting': 'LgoSetting',
  '/pharmacy/kopilot/invoices': 'KopilotInvoices',
  '/pharmacy/kopilot/invoices/$invoiceId': 'KopilotInvoice',
  // Legacy paths kept as redirect routes (FKS-8676); still in the route registry.
  '/pharmacy/real_invoices': 'KopilotInvoices',
  '/pharmacy/real_invoices/$invoiceId': 'KopilotInvoice',
  '/pharmacy/settings': undefined,
  '/pharmacy/settings/': undefined,
  '/pharmacy/settings/profile': 'SettingsProfile',
  '/pharmacy/settings/pharmacy': 'SettingsPharmacy',
  '/pharmacy/settings/banking': 'SettingsBanking',
  '/pharmacy/settings/billing': 'SettingsBilling',
  '/pharmacy/settings/group': 'SettingsGroup',
  '/pharmacy/settings/team': 'SettingsTeam',
  '/pharmacy/settings/konnect': 'SettingsKonnect',
  '/pharmacy/settings/kopilot': 'SettingsKopilot',
  '/pharmacy/settings/extractor': 'SettingsExtractor',
  '/pharmacy/settings/stripe': 'SettingsStripe',
  '/pharmacy/settings/note-templates': 'SettingsNoteTemplates',
  '/pharmacy/settings/preferences': 'SettingsPreferences',
  '/pharmacy/settings/stock-konnect': 'SettingsStockKonnect',
  '/pharmacy/stripe': 'Stripe',
  '/pharmacy/stripe/billing': 'StripeBillingForm',
  '/pharmacy/trades': 'Trades',
  '/pharmacy/trades/$id': 'Trade',
  '/pharmacy/trades/new': 'TradeNew',
  '/secured': undefined,
  '/secured/claims/$id': 'SecuredLabClaim',
  '/securedaccess/trades': undefined,
  '/stripe/state': 'StripeState',
  '/strong_authentication': undefined,
  '/user/pharmacy': 'EditCurrentPharmacy',
  '/user/profile': 'EditCurrentUser',
  '/verify_pharmacy_email': 'VerifyPharmacyEmail',
  '/pms_activation': 'PmsActivation',
} as const;

export const useTrackPage = () => {
  const trackingProperties = useTrackingContext();
  const authToken = useAuthStore((state) => state.userToken);
  const currentUser = useCurrentUser();
  const routeId = useMatches({ select: (matches) => matches.at(-1)?.routeId });
  const path = useMatches({ select: (matches) => matches.at(-1)?.pathname });
  const params = useMatches({ select: (matches) => matches.at(-1)?.params });
  const trackedUserId = tracker.user?.()?.id();
  const isTrackingEnabled = trackedUserId && authToken && currentUser?.id;

  useEffect(() => {
    // tracking of the opening page will be done via a retrigger of this useEffect once the initialization if done and the tracking enabled
    if (!isTrackingEnabled) return;

    const { currentPath, setPaths } = useNavigationStore.getState();

    try {
      const name = routeId ? trackedNameByRoute[routeId] : undefined;

      if (!name) return;

      tracker.page(name, {
        ...params,
        ...trackingProperties,
        ...(currentPath && { fromPath: currentPath }),
      });
    } finally {
      // will be triggered once per path anyway, even if the tracking is not yet enabled on the first render
      setPaths(path);
    }
  }, [isTrackingEnabled, trackingProperties, params, routeId, path]);
};
